/**
 * Business / operator info hooks.
 *
 * Customer-facing:
 *   - `useBusinessInfo()` — `GET /api/v1/business-info` (public,
 *     non-sensitive subset). Used by Footer, Contact, GDPR pages.
 *
 * Admin:
 *   - `useAdminBusinessInfo()` — `GET /api/v1/admin/business-info`
 *     (full document, requires admin role).
 *   - `useUpdateBusinessInfo()` — `PUT /api/v1/admin/business-info`
 *     to replace the document; invalidates both keys on success.
 *
 * The public + admin shapes intentionally differ — the public one
 * drops banking + e-mail-sender fields. See
 * `app.services.business_info` on the backend.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

import { queryKeys } from "./keys";

import type { WeeklySchedule } from "@/api/queries/admin/machines";

const API = "/api/v1";

export interface PublicBusinessInfo {
  legal_name: string;
  street: string;
  city: string;
  postal_code: string;
  country_code: string;
  ico: string;
  dic: string | null;
  support_email: string;
  phone: string | null;
  business_hours: string | null;
  pickup_address: string | null;
  pickup_hours: string | null;
  /** Hodiny osobního odběru jako týdenní rozvrh (tvar rozvrhu stroje).
   *  Má přednost před volným textem ``pickup_hours``. */
  pickup_schedule?: WeeklySchedule | null;
  vat_payer: boolean;
  currency: string;
  /** What the button at the end of the configurator does. Public
   *  because both the storefront and the embedded configurator have to
   *  know which button to draw before the visitor is anywhere near an
   *  account. */
  checkout_mode: "payment" | "inquiry" | "both";
  /** KAN-993 — komu se v pokladně nabídne „převodem na fakturu" vedle
   * karty. 🚨 NEPLATÍ, když tenant kartu přijmout neumí: tam je převod
   * jediná cesta a nabídne se každému bez ohledu na tuhle hodnotu. */
  bank_transfer_policy: "off" | "company_accounts" | "signed_in" | "all";
  /** KAN-1432 — komu tenant prodává (odpověď z průvodce). Rozhoduje, jestli
   *  obchod ukazuje ceny s DPH, nebo bez. ``null`` = neodpověděl. */
  buyer_segments?: "b2b" | "b2c" | "both" | null;
  /** Sazba DPH první aktivní země dopravy — TÝŽ náhled, se kterým počítá
   *  souhrn košíku. ``null`` = tenant není plátce nebo nemá žádnou zemi,
   *  takže není z čeho cenu s DPH spočítat. */
  default_vat_rate_pct?: string | number | null;
  /** KAN-1935 — jméno země sídla per jazyk z platformního katalogu zemí.
   *  Prázdné = země nevyplněná nebo neznámá. */
  country_name_i18n?: Record<string, string>;
}

export interface BusinessInfo extends PublicBusinessInfo {
  bank_account_iban: string | null;
  bank_swift: string | null;
  /** KAN-1489 — tenantní výchozí lhůta splatnosti ve dnech, kterou doklad
   *  dostane, když kupující není firemní účet s vlastní lhůtou
   *  (``CustomerOrg.default_due_days`` vyhrává). ``null`` = tenant ji
   *  neurčil a doklad splatnost NEUVEDE. ``0`` je „splatné ihned",
   *  ne vypnutí. **Není veřejné** — je to účetní nastavení prodávajícího. */
  default_due_days: number | null;
  /** KAN-1898 — kolik dní po splatnosti dostane zákazník (jedinou) upomínku
   *  k nezaplacené zálohovce. Výchozí 3; ``null`` = upomínky vypnuté. */
  proforma_reminder_days_after_due?: number | null;
  /** KAN-1898 — po kolika dnech po splatnosti se nezaplacená objednávka sama
   *  zruší. ``null`` = vypnuto (výchozí). */
  unpaid_order_cancel_days_after_due?: number | null;
  /** KAN-1850 — IANA pásmo obchodu (``Europe/Prague``). ``null`` = výslovně
   *  nenastaveno, pásmo se odvodí z domovské země (jinak UTC). */
  timezone?: string | null;
}

/** KAN-1850 — nabídka časových pásem pro Profil firmy a krok Firma. */
export interface TimezoneOptions {
  /** Pásma domovské země (z ``zone.tab``). */
  country_zones: string[];
  /** Jediné pásmo země; ``null`` u země s více pásmy (nehádá se). */
  default: string | null;
  all_zones: string[];
}

export function useTimezoneOptions(countryCode: string | null | undefined) {
  const code = (countryCode ?? "").toUpperCase();
  return useQuery<TimezoneOptions>({
    queryKey: ["admin", "tenant-business-info", "timezone-options", code],
    queryFn: () =>
      apiFetch<TimezoneOptions>(
        `${API}/admin/tenant-business-info/timezone-options?country_code=${encodeURIComponent(code)}`,
      ),
    staleTime: 10 * 60 * 1000,
  });
}

export function useBusinessInfo(
  options?: Omit<UseQueryOptions<PublicBusinessInfo>, "queryKey" | "queryFn">,
) {
  return useQuery<PublicBusinessInfo>({
    queryKey: queryKeys.businessInfo(),
    queryFn: () => apiFetch<PublicBusinessInfo>(`${API}/business-info`),
    // Footer / contact pages don't need second-by-second freshness.
    // Match the BE Cache-Control header (10 min).
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

export function useAdminBusinessInfo(
  options?: Omit<UseQueryOptions<BusinessInfo>, "queryKey" | "queryFn">,
) {
  return useQuery<BusinessInfo>({
    queryKey: queryKeys.adminBusinessInfo(),
    queryFn: () => apiFetch<BusinessInfo>(`${API}/admin/business-info`),
    staleTime: 60_000,
    ...options,
  });
}

export function useUpdateBusinessInfo(
  options?: UseMutationOptions<BusinessInfo, Error, BusinessInfo>,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<BusinessInfo, Error, BusinessInfo>({
    mutationFn: (payload) =>
      apiFetch<BusinessInfo>(`${API}/admin/business-info`, {
        method: "PUT",
        body: payload,
      }),
    ...restOptions,
    onSuccess: (...args) => {
      const saved = args[0];
      qc.setQueryData(queryKeys.adminBusinessInfo(), saved);
      // Invalidate the public projection too so the footer / contact
      // pages re-fetch and show the new identity.
      qc.invalidateQueries({ queryKey: queryKeys.businessInfo() });
      callerOnSuccess?.(...args);
    },
  });
}

/**
 * Tenant admin: read the calling tenant's business-info (or the
 * platform default if the tenant hasn't saved their own yet — the
 * BE merges those two for us).
 */
export function useAdminTenantBusinessInfo(
  options?: Omit<UseQueryOptions<BusinessInfo>, "queryKey" | "queryFn">,
) {
  return useQuery<BusinessInfo>({
    queryKey: queryKeys.adminTenantBusinessInfo(),
    queryFn: () =>
      apiFetch<BusinessInfo>(`${API}/admin/tenant-business-info`),
    staleTime: 60_000,
    ...options,
  });
}

/**
 * Tenant admin: replace the calling tenant's business-info row.
 * Always writes to the caller's resolved org_id server-side; the
 * frontend can't pick which tenant gets edited.
 */
export function useUpdateTenantBusinessInfo(
  options?: UseMutationOptions<BusinessInfo, Error, BusinessInfo>,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<BusinessInfo, Error, BusinessInfo>({
    mutationFn: (payload) =>
      apiFetch<BusinessInfo>(`${API}/admin/tenant-business-info`, {
        method: "PUT",
        body: payload,
      }),
    ...restOptions,
    onSuccess: (...args) => {
      const saved = args[0];
      qc.setQueryData(queryKeys.adminTenantBusinessInfo(), saved);
      // Public footer reads through the same /business-info endpoint
      // (now tenant-scoped); invalidate so it picks up the new row.
      qc.invalidateQueries({ queryKey: queryKeys.businessInfo() });
      callerOnSuccess?.(...args);
    },
  });
}

/** The five fields a shop introduces itself as SELLER with (KAN-1107),
 *  plus the optional seller details of the start-up step (KAN-1322). */
export interface TenantIdentityPatch {
  legal_name?: string;
  ico?: string;
  street?: string;
  city?: string;
  postal_code?: string;
  dic?: string;
  country_code?: string;
  /** „E-mail pro objednávky". */
  support_email?: string;
  phone?: string;
  /** KAN-1453 — suggested from the country in the start-up step, shown
   *  there, and written only when given. */
  currency?: string;
  /** KAN-1850 — IANA pásmo obchodu; zapisuje se jen je-li zadané. */
  timezone?: string;
  /** KAN-1865 — hodiny osobního odběru z průvodce; zapisují se jen jsou-li
   *  zadané (vynechání stávající rozvrh nemaže). */
  pickup_schedule?: WeeklySchedule;
}

/**
 * Write ONLY the seller-identity fields.
 *
 * The start-up step collects five fields and nothing else, so it must not
 * PUT a whole ``BusinessInfo`` — that would blank every key the form never
 * showed (``checkout_mode``, ``currency``, ``bank_account_iban`` …). The BE
 * does a read-modify-write of those five and carries the rest over.
 *
 * Same cache fan-out as the full PUT: the saved document replaces the admin
 * copy and the public footer's read is invalidated.
 */
export function useUpdateTenantIdentity(
  options?: UseMutationOptions<BusinessInfo, Error, TenantIdentityPatch>,
) {
  const qc = useQueryClient();
  const { onSuccess: callerOnSuccess, ...restOptions } = options ?? {};
  return useMutation<BusinessInfo, Error, TenantIdentityPatch>({
    mutationFn: (payload) =>
      apiFetch<BusinessInfo>(`${API}/admin/tenant-business-info/identity`, {
        method: "PATCH",
        body: payload,
      }),
    ...restOptions,
    onSuccess: (...args) => {
      const saved = args[0];
      qc.setQueryData(queryKeys.adminTenantBusinessInfo(), saved);
      qc.invalidateQueries({ queryKey: queryKeys.businessInfo() });
      callerOnSuccess?.(...args);
    },
  });
}
